import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pb } from '../lib/pb';
import { setLanguage, type Lang } from '../i18n';
import { parseCoordinates } from '../geo/format';
import { geometryAnchor } from '../geo/measure';
import { flyTo, isAdmin, setState, useStore } from '../store/store';
import { openMap } from '../store/data';
import { Icon } from './Icon';

export function TopBar() {
  const { t, i18n } = useTranslation();
  const user = useStore((s) => s.user);
  const maps = useStore((s) => s.maps);
  const mapId = useStore((s) => s.mapId);
  const realtime = useStore((s) => s.realtime);
  const features = useStore((s) => s.features);
  const view = useStore((s) => s.view);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (needle.length < 2) return [];
    return Object.values(features)
      .filter(
        (f) =>
          f.name.toLowerCase().includes(needle) ||
          f.key.includes(needle) ||
          f.tags?.some((tag) => tag.toLowerCase().includes(needle)),
      )
      .slice(0, 12);
  }, [q, features]);
  const coords = parseCoordinates(q);

  const changeLang = (lang: Lang) => {
    setLanguage(lang);
    if (user)
      void pb
        .collection('users')
        .update(user.id, { lang })
        .catch(() => undefined);
  };

  const go = (id: string) => {
    const f = features[id];
    setState({ selectedId: id });
    const a = f && geometryAnchor(f.geometry);
    if (a) flyTo(a.x, a.z);
    setOpen(false);
  };

  return (
    <header className="topbar">
      <div className="brand">{t('app.title')}</div>
      <div className="view-switch" role="tablist">
        {(['map', 'data'] as const).map((v) => (
          <button
            key={v}
            role="tab"
            aria-selected={view === v}
            className={view === v ? 'active' : ''}
            onClick={() => setState({ view: v, adminOpen: null, tool: { kind: 'none' } })}
          >
            {t(`top.views.${v}`)}
          </button>
        ))}
      </div>
      {view === 'map' && (
        <>
          <select
            value={mapId ?? ''}
            onChange={(e) => void openMap(e.target.value)}
            aria-label={t('top.map')}
          >
            {maps.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <div className="search">
            <Icon id="search" />
            <input
              value={q}
              placeholder={t('top.searchPlaceholder')}
              onChange={(e) => {
                setQ(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 150)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (coords) flyTo(coords.x, coords.z, 2);
                  else if (results[0]) go(results[0].id);
                }
              }}
            />
            {open && (coords || results.length > 0 || q.trim().length >= 2) && (
              <div className="search-results">
                {coords && (
                  <button onMouseDown={() => flyTo(coords.x, coords.z, 2)}>
                    {t('top.jumpTo', { x: coords.x, z: coords.z })}
                  </button>
                )}
                {results.map((f) => (
                  <button key={f.id} onMouseDown={() => go(f.id)}>
                    <span>{f.name}</span>
                    <span className="muted mono small">{f.key}</span>
                  </button>
                ))}
                {!coords && results.length === 0 && <div className="muted pad">{t('top.noResults')}</div>}
              </div>
            )}
          </div>
        </>
      )}
      <div className="spacer" />
      <span className={`rt-dot ${realtime}`} title={t(`top.realtime.${realtime}`)} />
      <div className="lang-switch">
        {(['sv', 'en'] as const).map((l) => (
          <button key={l} className={i18n.language === l ? 'active' : ''} onClick={() => changeLang(l)}>
            {l.toUpperCase()}
          </button>
        ))}
      </div>
      {isAdmin(user) && (
        <button
          className="ghost"
          onClick={() => setState({ adminOpen: view === 'data' ? 'gamedata' : 'layers' })}
        >
          <Icon id="tool-calibrate" /> {t('admin.title')}
        </button>
      )}
      <div className="user">
        <span>{user?.name || user?.email}</span>
        <span className="role">{t(`roles.${user?.role ?? 'viewer'}`)}</span>
        <button className="ghost icon-only" title={t('top.logout')} onClick={() => pb.authStore.clear()}>
          <Icon id="logout" />
        </button>
      </div>
    </header>
  );
}
